import {NgTemplateOutlet} from "@angular/common";
import {booleanAttribute, Component, computed, input} from "@angular/core";
import type {Optional} from "../../helpers/TypeHelpers";

enum LoadingIndicatorPlacement {
  Standalone,
  TableRow,
  Button,
}

export type LoadingIndicatorColorVariant =
  "primary" | "primary-emphasis"
  | "secondary" | "secondary-emphasis"
  | "success" | "success-emphasis"
  | "danger" | "danger-emphasis"
  | "warning" | "warning-emphasis"
  | "info" | "info-emphasis"
  | "light" | "light-emphasis"
  | "dark" | "dark-emphasis"
  | "body" | "body-emphasis" | "body-secondary" | "body-tertiary"
  | "black" | "black-50"
  | "white" | "white-50"
  | "muted";

export abstract class BaseLoadingIndicator {
  protected readonly LoadingIndicatorPlacement = LoadingIndicatorPlacement;
  protected readonly loadingText = $localize`:@@spinner.loading-text:Loading…`;

  protected readonly colorClass = computed(() => this._colorVariant === undefined ? undefined : `text-${this._colorVariant}`);

  protected abstract get placement(): LoadingIndicatorPlacement;

  protected abstract get _useSmall(): boolean;

  // Only relevant for buttons
  protected abstract get _isLoading(): boolean;

  // Not relevant for buttons
  protected abstract get _colorVariant(): Optional<LoadingIndicatorColorVariant>;
}

@Component({
  selector: "rf-loading-indicator",
  templateUrl: "./loading-indicator.html",
  imports: [
    NgTemplateOutlet,
  ],
})
export class StandaloneLoadingIndicator extends BaseLoadingIndicator {
  protected override get placement(): LoadingIndicatorPlacement {
    return LoadingIndicatorPlacement.Standalone;
  }

  public readonly small = input(false, {transform: booleanAttribute});
  public readonly color = input<LoadingIndicatorColorVariant>("primary");

  protected override get _useSmall(): boolean {
    return this.small();
  }

  protected override get _colorVariant(): Optional<LoadingIndicatorColorVariant> {
    return this.color();
  }

  protected override readonly _isLoading = true;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "tr[rfLoadingIndicator]",
  templateUrl: "./loading-indicator.html",
  imports: [
    NgTemplateOutlet,
  ],
})
export class TableRowLoadingIndicator extends BaseLoadingIndicator {
  protected override get placement(): LoadingIndicatorPlacement {
    return LoadingIndicatorPlacement.TableRow;
  }

  public readonly color = input<LoadingIndicatorColorVariant>("primary");

  protected override readonly _useSmall = false;
  protected override readonly _isLoading = true;

  protected override get _colorVariant(): Optional<LoadingIndicatorColorVariant> {
    return this.color();
  }
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "button[rfLoadingIndicator]",
  templateUrl: "./loading-indicator.html",
  imports: [
    NgTemplateOutlet,
  ],
})
export class ButtonLoadingIndicator extends BaseLoadingIndicator {
  protected override get placement(): LoadingIndicatorPlacement {
    return LoadingIndicatorPlacement.Button;
  }

  public readonly isLoading = input.required({alias: "rfLoadingIndicator", transform: booleanAttribute});

  protected override readonly _useSmall = true;
  protected override readonly _colorVariant: Optional<LoadingIndicatorColorVariant> = undefined;

  protected override get _isLoading(): boolean {
    return this.isLoading();
  }
}
